/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ---------------------------------------------------------------------------------------------
 */

import {
  connectTeamsComponent,
  IContext,
  ITeamsThemeContextProps,
  ThemeStyle
} from 'msteams-ui-components-react';
import * as React from 'react';
import { keyframes, style } from 'typestyle';
import './teams-spinner.scss';

export interface TeamsSpinnerProps {
  className?: string;
  small?: boolean;
  text?: string;
}

class TeamsSpinnerInner extends React.Component<TeamsSpinnerProps & ITeamsThemeContextProps> {
  public render() {
    let className;
    if (this.props.className) {
      className = `${this.props.className} ${styles(this.props.context, this.props.small)}`;
    } else {
      className = styles(this.props.context, this.props.small);
    }

    let spinnerColor = this.props.context.colors.light.brand00;
    if (this.props.context.style === ThemeStyle.Dark) {
      spinnerColor = this.props.context.colors.dark.white;
    } else if (this.props.context.style === ThemeStyle.HighContrast) {
      spinnerColor = this.props.context.colors.highContrast.white;
    }

    return (
      <div className="teams-spinner-inner">
        {
          this.props.text
            ? <div className="flex row" style={{ color: spinnerColor }}>
              <span className={className} />
              <span className="text-indicator">{this.props.text}</span>
            </div>
            : <span className={className} />
        }
      </div>
    );
  }
}

export const TeamsSpinner = connectTeamsComponent(TeamsSpinnerInner);

function styles(context: IContext, small?: boolean) {
  const { rem } = context;
  let color = context.colors.light.brand00;
  if (context.style === ThemeStyle.Dark) {
    color = context.colors.dark.white;
  } else if (context.style === ThemeStyle.HighContrast) {
    color = context.colors.highContrast.white;
  }

  const spinAnimationName = keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  });

  if (small) {
    return style({
      display: 'inline-block',
      border: `solid ${rem(0.1)} transparent`,
      borderTop: `solid ${rem(0.1)} ${color}`,
      borderRadius: '50%',
      width: rem(1.5),
      height: rem(1.5),
      animation: `${spinAnimationName} 2s linear infinite`
    });
  }

  return style({
    display: 'block',
    border: `solid ${rem(0.2)} transparent`,
    borderTop: `solid ${rem(0.2)} ${color}`,
    borderRadius: '50%',
    width: rem(3),
    height: rem(3),
    animation: `${spinAnimationName} 2s linear infinite`
  });
}
